import { useState } from "react";
import { Lightning, Plus, CaretLeft, SignOut, PencilSimple, DotsThreeVertical, Stack, DotsSixVertical, Trash } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { resolveIcon } from "./IconPicker";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

function DeckItem({
  deck,
  idx,
  active,
  collapsed,
  hoverIdx,
  setHoverIdx,
  onSelectDeck,
  onEditDeck,
  onDeleteDeck,
  isDraggingTile,
}) {
  const Icon = resolveIcon(deck.icon);
  const sortableId = `deck-${deck.deck_id}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  // canDrop = there's a tile being dragged from the grid (not a deck being reordered)
  const canDrop = isDraggingTile && !active;
  const tileIsHovering = isOver && isDraggingTile;

  return (
    <div
      ref={setNodeRef}
      style={style}
      key={deck.deck_id}
      onMouseEnter={() => setHoverIdx(idx)}
      onMouseLeave={() => setHoverIdx(null)}
      className={`relative rounded-xl transition group ${
        canDrop ? "ring-1 ring-dashed ring-[#FACC15]/40" : ""
      } ${tileIsHovering ? "ring-2 ring-[#FACC15] bg-[#FACC15]/15 scale-[1.02]" : ""}`}
      data-testid={`deck-droppable-${deck.deck_id}`}
    >
      <button
        data-testid={`deck-item-${deck.deck_id}`}
        onClick={() => onSelectDeck(deck.deck_id)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
          active
            ? "bg-white/8 border border-white/10"
            : "hover:bg-white/5 border border-transparent"
        }`}
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 deck-tile"
          style={{ backgroundColor: deck.color }}
        >
          <Icon size={18} weight="fill" className="text-white" />
        </div>
        {!collapsed && (
          <span
            className={`flex-1 truncate text-sm font-heading font-semibold ${
              active ? "text-white" : "text-white/70"
            }`}
          >
            {deck.name}
          </span>
        )}
        {!collapsed && active && !tileIsHovering && (
          <span className="w-1.5 h-1.5 rounded-full bg-[#FACC15]" />
        )}
        {tileIsHovering && (
          <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#FACC15] font-bold">
            ➜ Soltar
          </span>
        )}
      </button>

      {/* Drag handle to reorder decks */}
      {!collapsed && !isDraggingTile && (
        <button
          data-testid={`deck-drag-handle-${deck.deck_id}`}
          {...attributes}
          {...listeners}
          aria-label="Arrastrar para reordenar"
          className="absolute left-1 top-1/2 -translate-y-1/2 w-5 h-7 flex items-center justify-center text-white/30 hover:text-white/80 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <DotsSixVertical size={14} weight="bold" />
        </button>
      )}

      {/* Edit menu — always rendered (not hover-only) so it works on touch devices */}
      {!collapsed && !isDraggingTile && !isDragging && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`deck-menu-${deck.deck_id}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md bg-black/40 hover:bg-black/60 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Opciones de plataforma"
            >
              <DotsThreeVertical size={14} weight="bold" className="text-white" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={4}
            className="bg-deck-sidebar border-white/10 text-white min-w-[160px]"
          >
            <DropdownMenuItem
              data-testid={`deck-edit-${deck.deck_id}`}
              onClick={() => onEditDeck(deck)}
              className="cursor-pointer"
            >
              <PencilSimple size={14} className="mr-2" /> Editar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid={`deck-delete-${deck.deck_id}`}
              onClick={() => onDeleteDeck(deck)}
              className="cursor-pointer text-red-400 focus:text-red-400 focus:bg-red-500/10"
            >
              <Trash size={14} className="mr-2" /> Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export default function Sidebar({
  collapsed,
  onToggleCollapse,
  decks,
  activeDeckId,
  onSelectDeck,
  onAddDeck,
  onEditDeck,
  onDeleteDeck,
  isDraggingTile,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [hoverIdx, setHoverIdx] = useState(null);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside
      data-testid="sidebar"
      className={`${
        collapsed ? "w-[72px]" : "w-[260px]"
      } shrink-0 bg-deck-sidebar border-r border-white/5 flex flex-col transition-all duration-300 z-10 relative`}
    >
      {/* Logo */}
      <div className="px-5 py-6 flex items-center gap-3 border-b border-white/5">
        <div className="w-10 h-10 rounded-2xl bg-[#2563EB] flex items-center justify-center deck-tile shrink-0">
          <Lightning size={22} weight="fill" className="text-white" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FACC15]">PERSONAL</div>
            <div className="text-base font-heading font-bold text-white truncate">LaunchPad</div>
          </div>
        )}
      </div>

      {/* Workspace switcher */}
      <WorkspaceSwitcher collapsed={collapsed} />

      {/* Section header */}
      <div className="px-5 pt-6 pb-2 flex items-center justify-between">
        {!collapsed && (
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
            Plataformas
          </span>
        )}
        <button
          data-testid="add-deck-btn"
          onClick={onAddDeck}
          title="Nueva plataforma"
          className="ml-auto w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center transition"
        >
          <Plus size={14} weight="bold" className="text-white/70" />
        </button>
      </div>

      {/* Decks list */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 flex flex-col gap-1">
        <SortableContext
          items={decks.map((d) => `deck-${d.deck_id}`)}
          strategy={verticalListSortingStrategy}
        >
          {decks.map((deck, idx) => (
            <DeckItem
              key={deck.deck_id}
              deck={deck}
              idx={idx}
              active={deck.deck_id === activeDeckId}
              collapsed={collapsed}
              hoverIdx={hoverIdx}
              setHoverIdx={setHoverIdx}
              onSelectDeck={onSelectDeck}
              onEditDeck={onEditDeck}
              onDeleteDeck={onDeleteDeck}
              isDraggingTile={isDraggingTile}
            />
          ))}
        </SortableContext>
        {decks.length === 0 && !collapsed && (
          <div className="px-3 py-6 text-center text-xs font-mono text-white/40">
            Sin plataformas. Pulsa + para crear una.
          </div>
        )}
      </nav>

      {/* Footer: user + logout */}
      <div className="border-t border-white/5 p-3">
        <div className="flex items-center gap-3 p-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-9 h-9 rounded-full border border-white/10"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <Stack size={16} className="text-white/60" />
            </div>
          )}
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-xs font-heading font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] font-mono text-white/40 truncate">{user?.email}</div>
            </div>
          )}
          {!collapsed && (
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              title="Cerrar sesión"
              className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition"
            >
              <SignOut size={14} weight="bold" className="text-white/60" />
            </button>
          )}
        </div>

        <button
          data-testid="sidebar-toggle"
          onClick={onToggleCollapse}
          className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white/70 text-[10px] font-mono uppercase tracking-[0.18em] transition"
        >
          <CaretLeft
            size={12}
            weight="bold"
            className={`transition-transform ${collapsed ? "rotate-180" : ""}`}
          />
          {!collapsed && "Contraer"}
        </button>
      </div>
    </aside>
  );
}
